var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import clsx from 'clsx';
import { getDisabledElementProps, getHrefValue, getLinkProps, isNil, mergeProps, useClasses, useLayoutProps, useProps, } from '@hawkins/helpers';
import { IconCaretRight } from '@hawkins/icons/lib/iconCaretRight';
import { HawkinsGlobalClass, useTheme } from '@hawkins/styles';
import { Accordion, AccordionDetails, AccordionSummary, } from '../accordion';
import { useCombineRefs, useId, useMenuTrigger, useNavigationContext, useNestedMenu, usePress, useRefAssignment, } from '../hooks';
import { ListItemWrapper } from '../listItemWrapper';
import { MenuTriggerContext } from '../menu';
import { Text } from '../text';
import { Tooltip } from '../tooltip';
import { VisuallyHidden } from '../visuallyHidden';
import { NavigationMenuItemLevelContext } from './navigationMenuItem.context';
import { ROOT_PADDING_X_KEY, useNavigationMenuItemStyles, } from './navigationMenuItem.styles';
var displayName = 'NavigationMenuItem';
/**
 * `NavigationMenuItem` represents a menu item within a navigation component.
 * It may contain accordion content or a nested menu when inside a non-collapsed navigation area.
 *
 * `NavigationMenuItem` will render and style itself based on the current navigation context
 * (horizontal versus vertical, collapsed versus expanded versus tiered, etc.),
 * but it should also be contained inside a `NavigationMenuGroup`
 * in order for other relevant styling (margin, padding, width, etc.) to be applied.
 */
export var NavigationMenuItem = React.forwardRef(function (_a, ref) {
    var _b, _c, _d, _e, _f, _g, _h, _j;
    var children = _a.children, props = __rest(_a, ["children"]);
    // Props
    var _k = useProps(displayName, props), accordionContent = _k.accordionContent, accordionExpanded = _k.accordionExpanded, _l = _k.accordionProps, _m = _l === void 0 ? {} : _l, _o = _m.onChange, onChangeAccordionProp = _o === void 0 ? undefined : _o, accordionProps = __rest(_m, ["onChange"]), accordionDetailsProps = _k.accordionDetailsProps, accordionSummaryProps = _k.accordionSummaryProps, _p = _k.active, active = _p === void 0 ? false : _p, api = _k.api, classesProp = _k.classes, className = _k.className, closeOnAnyClick = _k.closeOnAnyClick, collapsedContent = _k.collapsedContent, component = _k.component, _q = _k.defaultAccordionExpanded, defaultAccordionExpanded = _q === void 0 ? false : _q, _r = _k.defaultNestedMenuOpen, defaultNestedMenuOpen = _r === void 0 ? false : _r, _s = _k.disableListItemWrapper, disableListItemWrapper = _s === void 0 ? false : _s, _t = _k.disabled, disabled = _t === void 0 ? false : _t, floatingUiProps = _k.floatingUiProps, href = _k.href, Icon = _k.icon, iconProps = _k.iconProps, defaultId = _k.id, labelProp = _k.label, _u = _k.labelNoWrap, labelNoWrap = _u === void 0 ? false : _u, labelProps = _k.labelProps, layout = _k.layout, listItemWrapperProps = _k.listItemWrapperProps, nestedMenuContent = _k.nestedMenuContent, nestedMenuOpen = _k.nestedMenuOpen, nestedMenuProps = _k.nestedMenuProps, onAnyClick = _k.onAnyClick, onClick = _k.onClick, orientationProp = _k.orientation, rootProps = _k.rootProps, styleProp = _k.style, subtitle = _k.subtitle, to = _k.to, tooltipProps = _k.tooltipProps, restProps = __rest(_k, ["accordionContent", "accordionExpanded", "accordionProps", "accordionDetailsProps", "accordionSummaryProps", "active", "api", "classes", "className", "closeOnAnyClick", "collapsedContent", "component", "defaultAccordionExpanded", "defaultNestedMenuOpen", "disableListItemWrapper", "disabled", "floatingUiProps", "href", "icon", "iconProps", "id", "label", "labelNoWrap", "labelProps", "layout", "listItemWrapperProps", "nestedMenuContent", "nestedMenuOpen", "nestedMenuProps", "onAnyClick", "onClick", "orientation", "rootProps", "style", "subtitle", "to", "tooltipProps"]);
    var id = useId(displayName, { defaultId: defaultId });
    var hasNestedMenu = !!nestedMenuContent;
    var hasSubtitle = !isNil(subtitle);
    var isAccordion = !!accordionContent;
    var isAccordionExpandedControlled = accordionExpanded !== undefined;
    var isAssumedLink = getHrefValue({ href: href, to: to }) !== null;
    var isClickable = !!onClick || isAssumedLink;
    var isHoverable = hasNestedMenu || isClickable;
    var label = labelProp !== null && labelProp !== void 0 ? labelProp : children;
    var Component = component || (isAssumedLink ? 'a' : isHoverable ? 'button' : 'div');
    // Context
    var open = React.useContext(MenuTriggerContext).open;
    var levelAbove = React.useContext(NavigationMenuItemLevelContext);
    var _v = useNavigationContext(), isCollapsedArea = _v.isCollapsedArea, isTransparent = _v.isTransparent, contextOrientation = _v.orientation, tier = _v.tier;
    var orientation = orientationProp || contextOrientation;
    var isHorizontal = orientation === 'horizontal';
    var isHorizontalTier2 = isHorizontal && tier === 2;
    var isVertical = orientation === 'vertical';
    // Refs
    var internalRef = React.useRef(null);
    var potentialListItemWrapperRootRef = React.useRef(null);
    var listItemWrapperRootRef = internalRef;
    // State
    var _w = __read(React.useState(defaultAccordionExpanded), 2), internalAccordionExpanded = _w[0], setInternalAccordionExpanded = _w[1];
    var onAccordionChange = React.useCallback(function (ev, expanded) {
        if (!isAccordionExpandedControlled)
            setInternalAccordionExpanded(expanded);
        onChangeAccordionProp === null || onChangeAccordionProp === void 0 ? void 0 : onChangeAccordionProp(ev, expanded);
    }, [isAccordionExpandedControlled, onChangeAccordionProp]);
    var imperativeApi = React.useMemo(function () { return ({
        toggleAccordion: function (arg) {
            if (arg === undefined) {
                setInternalAccordionExpanded(function (prev) { return !prev; });
            }
            else {
                setInternalAccordionExpanded(arg);
            }
        },
    }); }, []);
    useRefAssignment(imperativeApi, api);
    // Styles
    var baseClasses = useNavigationMenuItemStyles();
    var classes = useClasses(displayName, baseClasses, classesProp);
    var theme = useTheme();
    var basePaddingLeft = theme.hawkinsVariables.spacingValues[ROOT_PADDING_X_KEY];
    var levelIndentWidth = theme.hawkinsVariables.spacingValues.space5;
    var baseStyle = React.useMemo(function () { return (__assign({ paddingLeft: Math.max(levelIndentWidth * levelAbove, 0) + basePaddingLeft }, styleProp)); }, [basePaddingLeft, levelAbove, levelIndentWidth, styleProp]);
    var style = useLayoutProps({ layout: layout, style: baseStyle });
    // TODO(API): Nested menus aren't officially supported in this component by the design system.
    // This feature was added after a user request in the support channel.
    // In a future major version, check whether this feature is used at all and then ask the design team if we should remove this.
    var _x = useNestedMenu({
        classes: classes,
        closeOnAnyClick: closeOnAnyClick,
        defaultNestedMenuOpen: defaultNestedMenuOpen,
        disabled: disabled,
        floatingUiProps: floatingUiProps,
        isClickable: isClickable,
        nestedMenuContent: nestedMenuContent,
        nestedMenuOpen: nestedMenuOpen,
        nestedMenuProps: nestedMenuProps,
        onAnyClick: onAnyClick,
        onClick: onClick,
        parentItemId: id,
    }), derivedNestedMenuOpen = _x.derivedNestedMenuOpen, nestedMenuNode = _x.nestedMenuNode, onClickAugmented = _x.onClickAugmented, rootCallbacks = _x.rootCallbacks, rootRef = _x.rootRef;
    var pressProps = usePress({
        alwaysHandleClick: isAssumedLink && disabled,
        disabled: disabled,
        onPress: onClickAugmented,
    }).pressProps;
    var combinedRef = useCombineRefs([internalRef, ref, rootRef]);
    var accordionClassName = clsx(classes.accordion, (_b = {}, _b[classes.accordionActive] = active, _b));
    var accordionSummaryClassName = clsx(HawkinsGlobalClass.FOCUSABLE, classes.accordionSummary, (_c = {},
        _c[classes.accordionSummaryEnabled] = !disabled,
        _c));
    var accordionSummaryContentClassName = classes.accordionSummaryContent;
    var accordionSummaryToggleIconClassName = clsx(classes.accordionSummaryToggleIcon);
    var accordionSummaryToggleIconContainerClassName = classes.accordionSummaryToggleIconContainer;
    var caretClassName = clsx(classes.caret, (_d = {}, _d[classes.caretTransparent] = isTransparent, _d));
    var iconClassName = clsx(classes.icon, (_e = {}, _e[classes.iconTransparent] = isTransparent, _e));
    var iconContainerClassName = clsx(classes.iconContainer, (_f = {}, _f[classes.iconContainerVertical] = isVertical, _f));
    var labelClassName = clsx(classes.label, (_g = {}, _g[classes.labelTransparent] = isTransparent, _g));
    var subtitleClassName = clsx(classes.subtitle, (_h = {}, _h[classes.subtitleTransparent] = isTransparent, _h));
    var rootClassName = clsx(classes.HAWKINS_BASE, className, classes.root, (_j = {},
        _j[HawkinsGlobalClass.FOCUSABLE] = !isAccordion,
        _j[classes.rootActive] = active,
        _j[classes.rootClickable] = isClickable && !disabled,
        _j[classes.rootDisabled] = disabled,
        _j[classes.rootHoverable] = isHoverable && !disabled,
        _j[classes.rootVertical] = isVertical,
        // Use an `.active` class as a convenience,
        // since libraries like React Router use this class to designate active links.
        _j.active = active,
        _j));
    var accordionSummaryClasses = React.useMemo(function () { return ({
        content: accordionSummaryContentClassName,
        toggleIcon: accordionSummaryToggleIconClassName,
        toggleIconContainer: accordionSummaryToggleIconContainerClassName,
    }); }, [
        accordionSummaryContentClassName,
        accordionSummaryToggleIconClassName,
        accordionSummaryToggleIconContainerClassName,
    ]);
    // Content
    var _y = useMenuTrigger({
        defaultTriggerId: id,
        triggerIdNamespace: displayName,
    }), MenuCaretIcon = _y.CaretIcon, triggerAriaAttributes = _y.triggerAriaAttributes;
    if (isCollapsedArea && collapsedContent === null)
        return null;
    var CaretIcon = MenuCaretIcon || (hasNestedMenu ? IconCaretRight : undefined);
    var textContentNode = (_jsxs(_Fragment, { children: [_jsx(Text, __assign({ bold: true, className: labelClassName, colorVariant: disabled ? 'disabled' : 'faded', component: "div", level: isHorizontalTier2 ? 1 : 0, noWrap: labelNoWrap, variant: isHorizontalTier2 ? 'label' : 'body' }, labelProps, { children: label })), hasSubtitle && (_jsx(Text, __assign({ className: subtitleClassName, colorVariant: disabled ? 'disabled' : 'faded' }, { children: subtitle })))] }));
    var rootNode = (_jsxs(Component, __assign({}, triggerAriaAttributes, { "aria-current": active ? 'page' : undefined, "aria-expanded": hasNestedMenu ? derivedNestedMenuOpen : open || undefined, "aria-haspopup": hasNestedMenu || !isNil(open) ? 'menu' : undefined, className: rootClassName, id: id, ref: combinedRef, style: style }, getDisabledElementProps(Component, disabled, {
        // A `Tooltip` won't work properly on a natively disabled `<button />`, so always use `aria-disable` instead.
        alwaysUseAria: true,
    }), getLinkProps(Component, { href: href, to: to }), mergeProps(mergeProps(__assign(__assign({}, restProps), rootProps), rootCallbacks), pressProps), { children: [isCollapsedArea && collapsedContent, (!isCollapsedArea || !collapsedContent) && Icon && (_jsx("div", __assign({ className: iconContainerClassName }, { children: _jsx(Icon, __assign({ "aria-hidden": true, className: iconClassName }, iconProps)) }))), (!!label || hasSubtitle) &&
                (isCollapsedArea ? (_jsx(VisuallyHidden, { children: textContentNode })) : (_jsx("div", __assign({ className: classes.labelContainer }, { children: textContentNode })))), !isCollapsedArea && CaretIcon && _jsx(CaretIcon, { "aria-hidden": true, className: caretClassName, size: "small" }), nestedMenuNode] })));
    if (isAccordion) {
        rootNode = (_jsxs(Accordion, __assign({ className: accordionClassName, disabled: disabled, expanded: accordionExpanded !== null && accordionExpanded !== void 0 ? accordionExpanded : internalAccordionExpanded, onChange: onAccordionChange, ref: potentialListItemWrapperRootRef }, accordionProps, { children: [_jsx(AccordionSummary, __assign({ classes: accordionSummaryClasses, className: accordionSummaryClassName, size: "compact" }, accordionSummaryProps, { children: rootNode })), _jsx(AccordionDetails, __assign({ className: classes.accordionDetails }, accordionDetailsProps, { children: accordionContent }))] })));
        listItemWrapperRootRef = potentialListItemWrapperRootRef;
    }
    return (_jsx(NavigationMenuItemLevelContext.Provider, __assign({ value: levelAbove + 1 }, { children: _jsx(ListItemWrapper, __assign({ disabled: disableListItemWrapper, rootRef: listItemWrapperRootRef }, listItemWrapperProps, { children: isCollapsedArea ? (_jsx(Tooltip, __assign({ description: subtitle, label: label, placement: "right" }, tooltipProps, { children: rootNode }))) : (rootNode) })) })));
});
NavigationMenuItem.displayName = displayName;
